import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFormValues, change, untouch } from "redux-form";
import { RootState } from "../../types/redux";
import { FORM_NAMES } from "../../constants/forms";
import { BankAccountFormValues } from "../../types/forms";
import { COUNTRY_IDS } from "../../constants/countryCurrencies";
import { BANK_ACCOUNT_FORM_FIELDS, COUNTRY_2_DIGIT_ISO_CODES, FIELD_TYPES_BY_NAME, LOCAL_STORAGE_KEYS } from "../../constants/enums";
import useErrorModal from "../useErrorModal";
import CatalogApi from "../../api/catalog";
import { MoneyTransferPage, MoneyTransferSectionType } from "../../types/moneyTransfer";
import { UpdateBankAccountFormFields } from "../../actions/bankAccounts";

const useBeneficiaryCountryEffects = ({ pages, enabled }: { pages: MoneyTransferPage[], enabled: boolean }) => {
  const dispatch = useDispatch();
  const openErrorModal = useErrorModal();
  const formValues = useSelector((state: RootState) => getFormValues(FORM_NAMES.BANK_ACCOUNTS)(state)) as BankAccountFormValues;
  const prevCountryRef = useRef<string | undefined>(
    localStorage.getItem(LOCAL_STORAGE_KEYS.PREV_BENEFICIARY_COUNTRY) ?? undefined
  );

  const updatePagesState = (newSections: MoneyTransferSectionType[]) => {
    const newPages = [...pages];
    newPages[0] = { ...newPages[0], sections: newSections };

    dispatch(UpdateBankAccountFormFields(newPages));
  };

  /**
   * If the beneficiary country is the US, change the state/province field to a dropdown and fetch the states
   */
  const handleBeneficiaryCountryUS = async () => {
    try {
      const response = await CatalogApi.getUSStates();
      const newSections = pages[0].sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          let newField = { ...field };
          if (field.serviceProviderFieldId === BANK_ACCOUNT_FORM_FIELDS.BENEFICIARY_STATE_PROVINCE) {
            newField.datatype = FIELD_TYPES_BY_NAME.DROPDOWN;

            newField.options = response.map(state => ({ value: state.code, label: state.name }));
          }
          return newField;
        })
      }));

      updatePagesState(newSections);
    } catch (err) {
      openErrorModal(err);
    }
  }

  /**
   * If the beneficiary country is not the US, change the state/province field to a text field
   */
  const handleBeneficiaryCountryNonUS = () => {
    const newSections = pages[0].sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => {
        let newField = { ...field };
        if (field.serviceProviderFieldId === BANK_ACCOUNT_FORM_FIELDS.BENEFICIARY_STATE_PROVINCE) {
          newField.datatype = FIELD_TYPES_BY_NAME.TEXT;

          newField.options = [];

          dispatch(change(FORM_NAMES.BANK_ACCOUNTS, field.serviceProviderFieldId, null));

          /// The untouch is here so that redux form doesn't immediately mark this field as required once we clear it out.
          dispatch(untouch(FORM_NAMES.BANK_ACCOUNTS, field.serviceProviderFieldId));
        }
        return newField;
      })
    }));

    updatePagesState(newSections);
  }

  /**
     * If the beneficiary country is the US, change the state/province field to a dropdown and fetch the states
     * Else if the beneficiary country is not the US, change the state/province field to a text field
     * Need to track the previous value of the beneficiary country so we only make these changes when the country actually changes, not on every render after the country has been selected
     * Also only want to run this effect when the beneficiary form page is enabled, and not on flipping back and forth between the beneficiary country and bank details pages
     */
  useEffect(() => {
    if (!pages || pages.length === 0) return;

    const currentCountry = formValues?.BeneficiaryCountry;
    if (!currentCountry) return;

    // If not enabled, just track the current value so we don't
    // treat it as a "change" when enabled flips to true
    if (!enabled) {
      prevCountryRef.current = currentCountry;
      return;
    }

    if (currentCountry === prevCountryRef.current) return;
    prevCountryRef.current = currentCountry;
    localStorage.setItem(LOCAL_STORAGE_KEYS.PREV_BENEFICIARY_COUNTRY, currentCountry);

    if (formValues?.BeneficiaryCountry === String(COUNTRY_IDS.USA)) {
      handleBeneficiaryCountryUS();
    } else if (formValues?.BeneficiaryCountry !== COUNTRY_2_DIGIT_ISO_CODES.US) {
      handleBeneficiaryCountryNonUS();
    }


  }, [formValues?.BeneficiaryCountry]);

  // Clear the previous beneficiary country from local storage on unmount so that it doesn't interfere if the user goes through the flow multiple times in one session
  // Was experiencing an issue where if a user selected the US as the beneficiary country and went through flow
  // Then started the flow again and selected US again, the state/province field would still be a text field instead of a dropdown because the previous country stored in local storage was still the US, 
  // even though the current country was also the US so it didn't trigger the effect to change it to a dropdown
  useEffect(() => {
    return () => {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.PREV_BENEFICIARY_COUNTRY);
    };
  }, []);
}

export default useBeneficiaryCountryEffects;